import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import BasicCli from '../../main/components/basicCli/BasicCli';
import { ApiConstants } from '../../main/constants/ApiConstants';
import axiosInstance from '../../main/initializers/axios';
import IGridItems from '../../main/interfaces/IGridItems';
import IPagination from '../../main/interfaces/IPagination';
import EditModal from '../../main/components/modal/edit/EditModal';
import eFormType from '../../main/assets/enums/eFormType';
import GeneralTable from '../../main/components/grid/GeneralTable';
import { translateNotificationContent } from './notificationTranslation';
import './NotificationsGrid.css';

type NotificationRow = {
  id: number;
  title?: string;
  message: string;
  type?: string;
  entityId?: number;
  entityType?: string;
  severity?: string;
  audienceType?: string;
  unread: boolean;
  createdAt: string;
  readAt?: string;
};

const NotificationsGrid = () => {
  const { i18n } = useTranslation();
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [selected, setSelected] = useState<NotificationRow | null>(null);
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const getTable = useCallback(
    async (pag?: IPagination, gridOverride?: IGridItems[]) => {
      const effective = pag ?? pagination;
      const effectiveGrid = gridOverride ?? gridItems ?? [];
      const res = await axiosInstance.post(ApiConstants.notifications.listCt, {
        gridItems: effectiveGrid,
        pagination: effective,
      });
      setRows(res.data?.items ?? []);
      setGridItems(res.data?.gridItems ?? []);
      setPagination((prev) => ({
        ...prev,
        count: res.data?.pagination?.count ?? prev.count,
        page: res.data?.pagination?.page ?? prev.page,
        itemPerPage: res.data?.pagination?.itemPerPage ?? prev.itemPerPage,
        itemsPerPage: res.data?.pagination?.itemsPerPage ?? prev.itemsPerPage,
      }));
    },
    [gridItems, pagination],
  );

  useEffect(() => {
    void getTable();
  }, []);

  const markAsRead = async (row: NotificationRow) => {
    if (!row.unread) return;

    try {
      await axiosInstance.post(ApiConstants.notifications.read(row.id));
      setRows((prev) => prev.map((item) => (item.id === row.id ? { ...item, unread: false, readAt: new Date().toISOString() } : item)));
      setSelected((prev) => (prev?.id === row.id ? { ...prev, unread: false, readAt: new Date().toISOString() } : prev));
    } catch {
    }
  };

  const openDetails = async (row: NotificationRow) => {
    setSelected(row);
    await markAsRead(row);
  };

  const markAllAsRead = async () => {
    try {
      await axiosInstance.post(ApiConstants.notifications.readAll);
      setRows((prev) => prev.map((item) => ({ ...item, unread: false, readAt: item.readAt ?? new Date().toISOString() })));
      setSelected((prev) => (prev ? { ...prev, unread: false, readAt: prev.readAt ?? new Date().toISOString() } : prev));
    } catch {
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  const selectedItemPagination = (pag: IPagination) => {
    setPagination(pag);
    void getTable(pag);
  };

  const translatedRows = rows.map((row) => translateNotificationContent(row, i18n));
  const translatedSelected = selected
    ? translateNotificationContent(selected, i18n)
    : null;

  return (
    <BasicCli>
      <div className="ngWrap bcPageContent">
        <div className="ngContainer">
          <div className="bcPageHeader">
            <div>
              <h1 className="bcPageTitle">{i18n.t('notifications.title', { defaultValue: 'Notifications' })}</h1>
              <p className="ngSubtitle">
                {i18n.t('notifications.subtitle', {
                  defaultValue: 'Review company notifications and open full details.',
                })}
              </p>
            </div>

            <div className="bcPageHeaderActions">
              <button className="ui-control ui-btn" type="button" onClick={() => void getTable()}>
                <RefreshIcon fontSize="small" />
                {i18n.t('notifications.refresh', { defaultValue: 'Refresh' })}
              </button>
              <button className="ui-control ui-btn ui-btn--primary" type="button" onClick={() => void markAllAsRead()}>
                <MarkEmailReadIcon fontSize="small" />
                {i18n.t('notifications.markAllRead', { defaultValue: 'Mark all as read' })}
              </button>
            </div>
          </div>

          <div className=" ngCard">
            <GeneralTable
              data={translatedRows}
              columns={gridItems}
              gridEntity={18}
              title=""
              hasRp={true}
              selectedItem={(item) => {
                if (item) void openDetails(item);
              }}
              onEdit={(item) => {
                if (item) void openDetails(item);
              }}
              pagination={pagination}
              selectedItemPagination={(val) => selectedItemPagination(val)}
              onFilterClose={(nextGridItems) => {
                setGridItems(nextGridItems);
                void getTable(undefined, nextGridItems);
              }}
            />
          </div>
        </div>
      </div>

      {selected && (
        <EditModal
          title={i18n.t('notifications.details', { defaultValue: 'Notification details' })}
          width="760px"
          closeModal={() => setSelected(null)}
          setIsModalOpen={(value) => {
            if (!value) setSelected(null);
          }}
          onSubmitData={() => setSelected(null)}
          modalType={eFormType.Edit}
          hideApproveButton
        >
          <div className="ngDetails">
            <div className="ngDetailRow">
              <span className="ngDetailLabel">{i18n.t('notifications.titleColumn', { defaultValue: 'Title' })}</span>
              <span className="ngDetailValue">{translatedSelected?.title || '-'}</span>
            </div>
            <div className="ngDetailRow">
              <span className="ngDetailLabel">{i18n.t('notifications.message', { defaultValue: 'Message' })}</span>
              <span className="ngDetailValue isMessage">{translatedSelected?.message || '-'}</span>
            </div>
            <div className="ngDetailRow">
              <span className="ngDetailLabel">{i18n.t('notifications.severity', { defaultValue: 'Severity' })}</span>
              <span className="ngDetailValue">{selected?.severity || 'info'}</span>
            </div>
            <div className="ngDetailRow">
              <span className="ngDetailLabel">{i18n.t('notifications.audience', { defaultValue: 'Audience' })}</span>
              <span className="ngDetailValue">{selected?.audienceType || '-'}</span>
            </div>
            <div className="ngDetailRow">
              <span className="ngDetailLabel">{i18n.t('notifications.entity', { defaultValue: 'Entity' })}</span>
              <span className="ngDetailValue">{selected?.entityType ? `${selected.entityType} ${selected.entityId ?? ''}`.trim() : '-'}</span>
            </div>
            <div className="ngDetailRow">
              <span className="ngDetailLabel">{i18n.t('notifications.createdAt', { defaultValue: 'Created' })}</span>
              <span className="ngDetailValue">{formatDate(selected?.createdAt)}</span>
            </div>
            <div className="ngDetailRow">
              <span className="ngDetailLabel">{i18n.t('notifications.readAt', { defaultValue: 'Read at' })}</span>
              <span className="ngDetailValue">{formatDate(selected?.readAt) || '-'}</span>
            </div>
          </div>
        </EditModal>
      )}
    </BasicCli>
  );
};

export default NotificationsGrid;
